/**
 * TrackerQueueTable
 *
 * Flat order list for the Production Tracker "Order Queue" view — pre-batch
 * orders (getOrderQueue), NOT the same data as TrackerOrderTable (post-batch
 * monitoring via listTrackerOrders). Deliberately a LEAN view: no selection
 * checkboxes, no filter popovers, no Create Batch button — Coordinators still
 * use the separate /production/orders page for the full batching workflow.
 * Same visual language as TrackerOrderTable's "in progress" tab, extended
 * with a Put On Hold / Skip Hold kebab action instead of batch/override
 * actions.
 *
 * The kebab also carries the two location actions, Move and Split, for rows
 * the parent says are eligible (`canRelocate`). They are entry points only:
 * the parent opens the existing order detail sheet / resolve board. Same icons
 * and labels as IssueRowActionsMenu, so the action reads the same everywhere.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ClientOrgChip } from '@/components/ClientOrgChip';
import {
  useTrackerClientOrg,
  type TrackerClientOrg,
} from '../hooks/useTrackerClientOrg';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ChevronRight, MoreHorizontal, ExternalLink, Clock, Pause, PlayCircle, MapPin, GitBranch } from 'lucide-react';
import { format, differenceInHours } from 'date-fns';
import { cn } from '@/lib/utils';
import { formatOrderNumber } from '@threadheads/shared';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { SplitBadge } from '@/components/SplitBadge';
import type { QueuedOrder } from '@/features/production-orders/types';
import { OrderLineItemsTable } from '@/features/production-orders/components/orders/OrderLineItemsTable';
import { ExpandedRowPanel, ExpandedRowMessage } from './ExpandedOrderContent';
import {
  STATUS_LABEL_KEYS,
  statusColors,
  getFulfillmentType,
  getAgeColor,
} from '../lib/order-status-display';

// ============================================================================
// HOLD COUNTDOWN
// ============================================================================

function formatHoldRemaining(heldUntil: string): string | null {
  const remaining = new Date(heldUntil).getTime() - Date.now();
  if (remaining <= 0) return null;
  const minutes = Math.ceil(remaining / 60_000);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

function formatAge(orderDate: string): string {
  const hours = differenceInHours(new Date(), new Date(orderDate));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  return '<1h';
}

interface TrackerQueueTableProps {
  orders: QueuedOrder[];
  shopifyDomain?: string | null;
  isLoading?: boolean;
  onPutOnHold?: (order: QueuedOrder) => void;
  onSkipHold?: (order: QueuedOrder) => void;
  /** Open "Move to another location" for this order. */
  onMove?: (order: QueuedOrder) => void;
  /** Open "Split across locations" for this order. */
  onSplit?: (order: QueuedOrder) => void;
  /** Whether Move / Split may be offered on this row. Absent = never. */
  canRelocate?: (order: QueuedOrder) => boolean;
}

// ============================================================================
// EXPANDED ORDER CONTENT
// ============================================================================

/**
 * Pre-batch orders have no prod_garment_units yet (those are minted at batch
 * creation) — so unlike TrackerOrderTable's post-batch expand, there are no
 * garments to fetch here, and no siblings to roll up either. Render the order's
 * own line items instead (already on the getOrderQueue payload — no extra
 * request needed). The panel + message SHELLS are shared with the Orders tab
 * (ExpandedOrderContent) so the two expansions stay visually identical.
 */
function ExpandedQueueOrderContent({ order }: { order: QueuedOrder }) {
  const { t } = useTranslation('production');

  if (!order.line_items || order.line_items.length === 0) {
    return <ExpandedRowMessage>{t('tracker.orders.noLineItems')}</ExpandedRowMessage>;
  }

  return (
    <ExpandedRowPanel>
      <OrderLineItemsTable lineItems={order.line_items} />
    </ExpandedRowPanel>
  );
}

// ============================================================================
// QUEUE ROW + EXPANSION
// ============================================================================

/**
 * The row itself expands on Enter / Space (its own onKeyDown). Radix opens the
 * menu trigger, and fires a menu item, on keydown without stopping propagation,
 * and the menu content is a React child of the row even though it is portalled.
 * Without this a keyboard user opening the menu, or choosing an item, would
 * also expand or collapse the row. Same handling as IssueRowActionsMenu.
 */
const stopActivationKeys = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
};

function QueueRowGroup({
  order,
  isExpanded,
  onToggle,
  shopifyDomain,
  onPutOnHold,
  onSkipHold,
  onMove,
  onSplit,
  canRelocate,
  clientOrg,
}: {
  order: QueuedOrder;
  isExpanded: boolean;
  onToggle: () => void;
  shopifyDomain?: string | null;
  onPutOnHold?: (order: QueuedOrder) => void;
  onSkipHold?: (order: QueuedOrder) => void;
  onMove?: (order: QueuedOrder) => void;
  onSplit?: (order: QueuedOrder) => void;
  canRelocate?: (order: QueuedOrder) => boolean;
  clientOrg: TrackerClientOrg;
}) {
  const { t } = useTranslation('production');
  const shouldReduceMotion = useReducedMotion();
  const totalItems = order.line_items.reduce((sum, li) => sum + li.quantity, 0);
  const shopifyUrl = getShopifyAdminOrderUrl(shopifyDomain, order.shopify_order_id);
  const podItems = order.line_items
    .filter((li) => li.production_type === 'POD')
    .reduce((sum, li) => sum + li.quantity, 0);
  const papItems = order.line_items
    .filter((li) => li.production_type === 'PAP')
    .reduce((sum, li) => sum + li.quantity, 0);
  const fulfillment = getFulfillmentType(podItems, papItems);
  const ageColor = getAgeColor(order.order_date);
  const ageText = formatAge(order.order_date);
  const productionStatus = order.prod_order?.production_status ?? 'NEW';
  const isHeld = !!(order.held_until && new Date(order.held_until) > new Date());
  const holdText = isHeld ? formatHoldRemaining(order.held_until!) : null;
  const orderName = order.order_number ?? `#${order.shopify_order_id}`;
  // Which kebab groups this row actually renders. A separator is drawn only
  // BETWEEN two groups that are both present, so the menu never opens on a
  // leading separator or shows two in a row.
  const showShopifyItem = !!shopifyUrl;
  const showHoldItem = isHeld ? !!onSkipHold : !!onPutOnHold;
  const showRelocateItems = !!onMove && !!onSplit && !!canRelocate && canRelocate(order);
  // BLU-758: a pre-batch queue row carries no sibling roll-up (the other half
  // may not even have a prod_order yet), so the badge stays countless and
  // stateless — it says only what this payload can back up.
  const isSplit = order.is_intentional_split === true;
  const ariaLabel = [
    t('tracker.orders.rowAria', { order: orderName, items: totalItems }),
    isSplit ? t('tracker.orders.split.ariaSplit') : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <TableRow
        className={cn(
          'cursor-pointer hover:bg-muted/50 transition-colors group',
          isExpanded && 'bg-muted/30'
        )}
        onClick={onToggle}
        tabIndex={0}
        aria-label={ariaLabel}
        aria-expanded={isExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <TableCell className="w-10 px-3">
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              isExpanded && 'rotate-90'
            )}
          />
        </TableCell>
        <TableCell className="font-medium">
          {/* flex-wrap for the same reason as the Orders tab: TEST + Split can
              share this cell, and a nowrap cluster widens the column instead. */}
          <div className="flex flex-wrap items-center gap-2">
            {shopifyUrl ? (
              <a
                href={shopifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {formatOrderNumber(orderName)}
              </a>
            ) : (
              <span>{formatOrderNumber(orderName)}</span>
            )}
            {order.prod_order?.is_test && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600 dark:text-amber-400">
                TEST
              </Badge>
            )}
            {isSplit && <SplitBadge />}
            {/* BLU-367: whose order this is. Gated on isCrossOrg — own-org rows
                must render nothing, or every row in a single-tenant queue gets
                a meaningless badge. */}
            {clientOrg.isCrossOrg(order.organization_id) && (
              <ClientOrgChip label={clientOrg.labelFor(order.organization_id)} />
            )}
          </div>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {order.customer_name ?? '-'}
        </TableCell>
        <TableCell>
          <Badge variant="secondary" className={fulfillment.className}>
            {t(fulfillment.labelKey)}
          </Badge>
        </TableCell>
        <TableCell>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={cn('inline-flex items-center gap-1 text-xs cursor-default', ageColor)} onClick={(e) => e.stopPropagation()}>
                <Clock className="h-3.5 w-3.5" />
                {ageText}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {format(new Date(order.order_date), 'PPp')}
            </TooltipContent>
          </Tooltip>
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {totalItems}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1.5">
            <Badge
              variant="secondary"
              className={statusColors[productionStatus] ?? statusColors.NEW}
            >
              {STATUS_LABEL_KEYS[productionStatus] ? t(STATUS_LABEL_KEYS[productionStatus]) : productionStatus.replace(/_/g, ' ')}
            </Badge>
            {holdText && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-700 dark:text-amber-400 gap-1">
                <Pause className="h-3 w-3" />
                {t('orders.hold.pending', { time: holdText })}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              {/* Hidden until the row is hovered, but never while it has
                  keyboard focus or its menu is open: a keyboard user must be
                  able to see what they are about to press. The focus ring
                  itself is the Button's own. */}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity"
                onKeyDown={stopActivationKeys}
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('tracker.orders.orderActions')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onKeyDown={stopActivationKeys}>
              {showShopifyItem && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(shopifyUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {t('tracker.orders.viewInShopify')}
                </DropdownMenuItem>
              )}
              {showShopifyItem && showHoldItem && <DropdownMenuSeparator />}
              {!isHeld && onPutOnHold && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onPutOnHold(order);
                  }}
                >
                  <Pause className="h-4 w-4 mr-2" />
                  {t('orders.actions.putOnHold')}
                </DropdownMenuItem>
              )}
              {isHeld && onSkipHold && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onSkipHold(order);
                  }}
                >
                  <PlayCircle className="h-4 w-4 mr-2" />
                  {t('orders.actions.skipHold')}
                </DropdownMenuItem>
              )}
              {showRelocateItems && (showShopifyItem || showHoldItem) && <DropdownMenuSeparator />}
              {showRelocateItems && (
                <>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onMove(order);
                    }}
                  >
                    <MapPin className="h-4 w-4 mr-2" aria-hidden="true" />
                    {t('issues.rowActions.move')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onSplit(order);
                    }}
                  >
                    <GitBranch className="h-4 w-4 mr-2" aria-hidden="true" />
                    {t('issues.rowActions.split')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>

      {/* Expanded garments — rendered directly after this order row */}
      <AnimatePresence>
        {isExpanded && (
          <tr>
            <td colSpan={7} className="p-0">
              <motion.div
                initial={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                animate={shouldReduceMotion ? {} : { opacity: 1, height: 'auto' }}
                exit={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden bg-muted/10 border-b"
              >
                <ExpandedQueueOrderContent order={order} />
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TrackerQueueTable({
  orders,
  shopifyDomain,
  isLoading,
  onPutOnHold,
  onSkipHold,
  onMove,
  onSplit,
  canRelocate,
}: TrackerQueueTableProps) {
  // Resolved once here, not per row — one query subscription instead of one
  // per rendered order.
  const clientOrg = useTrackerClientOrg();
  const { t } = useTranslation('production');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  const toggleExpand = (orderId: string) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  if (isLoading) {
    return (
      <div className="rounded-md border">
        <div className="space-y-0">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-4 py-3.5 px-4 border-b last:border-0">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-6 w-12" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-6 w-8" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return null; // Parent handles empty state
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10"><span className="sr-only">{t('tracker.orders.header.expand')}</span></TableHead>
            <TableHead>{t('tracker.orders.header.order')}</TableHead>
            <TableHead>{t('tracker.orders.header.customer')}</TableHead>
            <TableHead>{t('tracker.orders.header.type')}</TableHead>
            <TableHead>{t('tracker.orders.header.age')}</TableHead>
            <TableHead className="text-right">{t('tracker.orders.header.items')}</TableHead>
            <TableHead>{t('tracker.orders.header.status')}</TableHead>
            <TableHead className="w-10"><span className="sr-only">{t('tracker.orders.header.actions')}</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => (
            <QueueRowGroup
              key={order.id}
              order={order}
              isExpanded={expandedOrderId === order.id}
              onToggle={() => toggleExpand(order.id)}
              shopifyDomain={shopifyDomain}
              onPutOnHold={onPutOnHold}
              onSkipHold={onSkipHold}
              onMove={onMove}
              onSplit={onSplit}
              canRelocate={canRelocate}
              clientOrg={clientOrg}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
